import React, { useEffect } from 'react';
import type { Nullable } from 'corgi-types';
import type { ReclamationReasonValue } from 'graphql-ts-types';

import ReclamationAlert from 'common/components/Reclamation/ReclamationAlert';
import type { ReasonsContent } from '../gql/query/types';
import { useReasonAssigner, useReasons } from '../hooks/reasons';
import type { BaseReclamationStepProps } from '../types/steps';
import type { ReasonsTrackingEventData } from '../utils/tracking';
import RadioGroup from './RadioGroup';

export interface ReclamationStepReasonsProps extends BaseReclamationStepProps {
  trackingEventData: ReasonsTrackingEventData;
}

const getRadioGroups = (
  { reasons }: ReasonsContent,
  currentValue: Nullable<string>,
  disabled: boolean
) =>
  reasons.map(({ title, values }) => ({
    title,
    radios: values
      .filter((value): value is ReclamationReasonValue => Boolean(value))
      .map(({ title: label, id: value }) => ({
        label,
        value,
        disabled,
        selected: value === currentValue,
      })),
  }));

const ReclamationStepReasons = ({
  type,
  alert,
  reclamationId,
  itemId,
  trackingEventData,
  setIsNextDisabled,
  setHeaderProps,
  setOnSubmit,
}: ReclamationStepReasonsProps) => {
  const { reasonId, reasonsContent, internalAlert, setReasonId } = useReasons({
    reclamationId,
    itemId,
    alert,
  });

  const isFetching = useReasonAssigner({
    reclamationId,
    itemId,
    reasonId,
    trackingEventData,
    setIsNextDisabled,
    setOnSubmit,
  });

  useEffect(() => {
    setHeaderProps({
      title: reasonsContent?.title,
    });
  }, [reasonsContent]);

  const showRadios = !!reasonsContent && reasonsContent.reasons.length;

  return (
    <section data-testid={`reclamation-step-${type}-${itemId}`}>
      <ReclamationAlert {...internalAlert} />
      {showRadios && (
        <RadioGroup
          name="reason-input"
          currentValue={reasonId}
          onChange={setReasonId}
          legend={reasonsContent.title}
          groups={getRadioGroups(reasonsContent, reasonId, isFetching)}
        />
      )}
    </section>
  );
};

export default ReclamationStepReasons;
